import { FocusableHeader } from "./FocusableHeader";
import { ActionType } from "../@types";
import { useAppContext } from "../helpers/context-hooks/useAppContext";
import { useRouteMatch } from "react-router-dom";
import { useQueryParams } from "../helpers/queryParamsHook";
import { type FC, type ReactNode, useEffect } from "react";

interface WithHeading {
  heading: string;
  headingClass?: string;
  headingId?: string;
  /**
   * Unless the component using `AppPage` isn't either a routed component or part
   * of Enrollment, both of which are scrolled to top by the `ScrollRestoration`
   * component, the `FocusableHeader` will only get focus on mount and not trigger a
   * scroll event. Pass this prop if you actually do want `FocusableHeader` to
   * trigger a scroll event.
   */
  scrollToFocusableHeader?: boolean;
}

interface WithoutHeading {
  heading?: never;
  headingClass?: never;
  headingId?: never;
  scrollToFocusableHeader?: never;
}

type HeadingProps = WithHeading | WithoutHeading;

export type AppPageProps = HeadingProps & {
  headerExtension?: ReactNode;
  hideLogin?: boolean;
  subHeading?: ReactNode;
  subHeadingClass?: string;
  outerClassName?: string;
  innerClassName?: string;
  bannerClassName?: string;
  title?: string;
  children: ReactNode;
};

export const AppPage: FC<AppPageProps> = props => {
  const {
    headerExtension,
    heading,
    headingClass,
    headingId,
    scrollToFocusableHeader = false,
    subHeading,
    subHeadingClass,
    outerClassName,
    innerClassName,
    title,
    children,
    bannerClassName,
  } = props;
  const { dispatch } = useAppContext();
  const match = useRouteMatch();
  useQueryParams();

  useEffect(() => {
    if (title) {
      document.title = title;
    } else if (heading) {
      document.title = heading;
    }
  }, [title, heading]);

  useEffect(() => {
    // Trigger only on page load
    dispatch({ type: ActionType.UPDATE_ROUTE_PARAMS, payload: match.params });
    // ! TODO: See MCT-11398
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This error has been grandfathered in and needs to be fixed.
  }, []);

  return (
    <>
      <div className={`appPage ${outerClassName ? outerClassName : ""}`}>
        {headerExtension}
        {(heading || subHeading) && (
          <div
            className={`appPage__banner ${
              bannerClassName ? bannerClassName : ""
            }`}
          >
            {heading && (
              <FocusableHeader
                className={headingClass || ""}
                id={headingId}
                scrollOnFocus={scrollToFocusableHeader}
                text={heading}
              />
            )}
            {subHeading && (
              <div
                className={
                  subHeadingClass || "ds-text-body--md ds-u-margin-top--1"
                }
              >
                {subHeading}
              </div>
            )}
          </div>
        )}
        <div
          className={`appPage__content ${innerClassName ? innerClassName : ""}`}
        >
          {children}
        </div>
      </div>
    </>
  );
};
